const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

const STAGING_API_BASE_URL =
  import.meta.env.VITE_STAGING_API_BASE_URL || 'https://api-staging.replya.app/api';

// True when the app is running against a local backend (localhost/127.0.0.1 URL).
export const isLocalBackend =
  /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/?/.test(API_BASE_URL);

export class ApiException extends Error {
  constructor(
    message: string,
    public statusCode?: number,
  ) {
    super(message);
    this.name = 'ApiException';
  }
}

class ApiClient {
  private static instance: ApiClient;
  private static stagingInstance: ApiClient;
  // Tutte le istanze condividono la stessa chiave admin (prod e staging usano lo stesso header).
  private static instances: ApiClient[] = [];
  private adminApiKey: string | null = null;

  private constructor(private readonly baseUrl: string) {
    ApiClient.instances.push(this);
  }

  static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient(API_BASE_URL);
    }
    return ApiClient.instance;
  }

  static getStagingInstance(): ApiClient {
    if (!ApiClient.stagingInstance) {
      ApiClient.stagingInstance = new ApiClient(STAGING_API_BASE_URL);
    }
    return ApiClient.stagingInstance;
  }

  setAdminApiKey(key: string | null) {
    for (const instance of ApiClient.instances) {
      instance.adminApiKey = key;
    }
  }

  private get headers(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    if (this.adminApiKey) {
      headers['x-admin-api-key'] = this.adminApiKey;
    }
    return headers;
  }

  async get(path: string, extraHeaders?: Record<string, string>): Promise<unknown> {
    const url = `${this.baseUrl}${path}`;
    console.debug('[ApiClient] GET', url);
    const response = await fetch(url, {
      method: 'GET',
      headers: { ...this.headers, ...extraHeaders },
    });
    return this.handleResponse(response);
  }

  async post(
    path: string,
    body?: Record<string, unknown>,
    extraHeaders?: Record<string, string>,
  ): Promise<unknown> {
    const url = `${this.baseUrl}${path}`;
    console.debug('[ApiClient] POST', url, body);
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...this.headers, ...extraHeaders },
      body: body ? JSON.stringify(body) : undefined,
    });
    return this.handleResponse(response);
  }

  async put(
    path: string,
    body?: Record<string, unknown>,
    extraHeaders?: Record<string, string>,
  ): Promise<unknown> {
    const url = `${this.baseUrl}${path}`;
    console.debug('[ApiClient] PUT', url, body);
    const response = await fetch(url, {
      method: 'PUT',
      headers: { ...this.headers, ...extraHeaders },
      body: body ? JSON.stringify(body) : undefined,
    });
    return this.handleResponse(response);
  }

  async patch(
    path: string,
    body?: Record<string, unknown>,
    extraHeaders?: Record<string, string>,
  ): Promise<unknown> {
    const url = `${this.baseUrl}${path}`;
    console.debug('[ApiClient] PATCH', url, body);
    const response = await fetch(url, {
      method: 'PATCH',
      headers: { ...this.headers, ...extraHeaders },
      body: body ? JSON.stringify(body) : undefined,
    });
    return this.handleResponse(response);
  }

  async delete(path: string, extraHeaders?: Record<string, string>): Promise<unknown> {
    const url = `${this.baseUrl}${path}`;
    console.debug('[ApiClient] DELETE', url);
    const response = await fetch(url, {
      method: 'DELETE',
      headers: { ...this.headers, ...extraHeaders },
    });
    return this.handleResponse(response);
  }

  private async handleResponse(response: Response): Promise<unknown> {
    const text = await response.text();

    if (response.status >= 200 && response.status < 300) {
      if (!text) return null;
      try {
        return JSON.parse(text);
      } catch (e) {
        console.error('[ApiClient] JSON decode error', e);
        throw new ApiException('Invalid JSON response', response.status);
      }
    }

    let message: string;
    if (text) {
      try {
        const decoded = JSON.parse(text) as Record<string, unknown>;
        message =
          (decoded.message as string) ||
          (decoded.error as string) ||
          `HTTP ${response.status}`;
      } catch {
        message = `HTTP ${response.status}: ${text}`;
      }
    } else {
      message = `HTTP ${response.status}`;
    }
    console.error('[ApiClient] API error', message);
    throw new ApiException(message, response.status);
  }
}

export const apiClient = ApiClient.getInstance();
export const stagingApiClient = ApiClient.getStagingInstance();
